import PageLoader from "@/components/loader/PageLoader";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import React, { useEffect, useState } from "react";

interface PrintableOrderDetailsProps {
  id: number;
}

const formatDeliveryAddress = (addressObj: any): string => {
  if (!addressObj) return "N/A";
  const { address, city, region, country, postal } = addressObj;
  return `${address}, ${city}, ${region}, ${country} ${postal}`.trim();
};

const PrintableOrderDetails: React.FC<PrintableOrderDetailsProps> = ({
  id,
}) => {
  const [order, setOrder] = React.useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [axiosInstance, loadingAxiosInstance] = useAxiosInstance();

  useEffect(() => {
    (async () => {
      if (loadingAxiosInstance || !axiosInstance) return;
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/api/v1/order/${id}`);
        setOrder(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [axiosInstance, loadingAxiosInstance, id]);

  return (
    <PageLoader isFetching={loading}>
      {order ? (
        <div className="mx-auto max-w-md rounded-lg border border-gray-300 bg-white p-6 shadow-md">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold uppercase">{order.order_type}</h2>
            <span className="text-2xl font-bold">{order.order_number}</span>
          </div>

          <div className="mb-4 border-b-2 border-dotted border-gray-400"></div>

          <div className="mb-4">
            <p>
              <span className="font-semibold">Name:</span>{" "}
              {order.customer_name || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Phone:</span>{" "}
              {order.phone_number || "N/A"}
            </p>
            <p>
              <span className="font-semibold">OrderID:</span> {order.order_name}
            </p>
            <p>
              <span className="font-semibold">{order.payment_status}:</span>{" "}
              {new Date(order.created_at).toLocaleString()}
            </p>
          </div>

          <div className="my-4 border-b-2 border-t-2 border-dotted border-gray-400 py-4">
            {order.order_items.map((item: any, index: number) => (
              <div key={`${item.product_id}-${index}`} className="mb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="font-semibold">{item.quantity}</span>
                    <span className="ml-2 font-semibold">
                      {item.product_name}
                    </span>
                  </div>
                  <span>${item.price_set}</span>
                </div>
                {item.options.map((option: any) => (
                  <div
                    key={option.id}
                    className="ml-4 flex justify-between text-sm"
                  >
                    <span>
                      {option.name} x{option.quantity}
                    </span>
                    <span>
                      {option.surcharge > 0 &&
                        ` +$${(option.surcharge * option.quantity).toFixed(2)}`}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {order.order_comments && (
            <div className="mb-4">
              <h3 className="font-semibold">Comments</h3>
              <p className="rounded border border-gray-300 bg-gray-50 p-2">
                {order.order_comments}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>${order.subtotal_price}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax:</span>
              <span>${order.tax || "0.00"}</span>
            </div>
            <div className="flex justify-between">
              <span>Tip:</span>
              <span>${order.tip || "0.00"}</span>
            </div>
            {order.points_discount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Points Off:</span>
                <span>-${order.points_discount}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Service Fee:</span>
              <span>${order.surcharge_fee || "0.00"}</span>
            </div>
            {order.order_type.toLowerCase() === "delivery" && (
              <div className="flex justify-between">
                <span>Delivery Fee:</span>
                <span>${order.delivery_fees || "N/A"}</span>
              </div>
            )}
            {order.promotion_name && (
              <div className="flex justify-between">
                <span>Promotion:</span>
                <span>{order.promotion_name}</span>
              </div>
            )}
            {order.promotion_code && (
              <div className="flex justify-between">
                <span>Promotion Code:</span>
                <span>{order.promotion_code}</span>
              </div>
            )}
            {order.promotion_discount_value && (
              <div className="flex justify-between">
                <span>Promotion Discount:</span>
                <span>-${order.promotion_discount_value}</span>
              </div>
            )}
            <div className="flex justify-between font-bold">
              <span>Total:</span>
              <span>${order.total_price}</span>
            </div>
          </div>

          {order.order_type.toLowerCase() === "delivery" && (
            <div className="mt-4">
              <p className="font-semibold">Delivery Address:</p>
              <p>{formatDeliveryAddress(order.delivery_address)}</p>
            </div>
          )}

          <div className="mt-6 border-t-2 border-dotted border-gray-400 pt-4 text-center">
            <span className="text-xl font-bold uppercase">
              {order.payment_status}
              {order.payment_status === "Partially Refunded" && (
                <span> - ${order.amount_refund} </span>
              )}
            </span>
          </div>
        </div>
      ) : (
        <p className="text-center">No order found</p>
      )}
    </PageLoader>
  );
};

export default PrintableOrderDetails;
